import type { CSSProperties } from 'react';
import { AxisLensSignature } from '../components/AxisLensCard';
import { Card } from '../components/Card';
import { ClaimCorrectionControl } from '../components/ClaimCorrectionControl';
import { CoachMark } from '../components/CoachMark';
import { DeclaredInputBanner } from '../components/DeclaredInputBanner';
import bannerStyles from '../components/DeclaredInputBanner.module.css';
import frontDoorStyles from '../components/FrontDoorCards.module.css';
import {
  ContactCard,
  FinaleCard,
  HomeCountryCard,
  PostalRadiusCard,
  PreferredNameCard,
  RelocationCard,
  StepFrame,
  WorkRightsCard,
} from '../components/frontDoorCardBodies';
import { JobFitRadar } from '../components/JobFitRadar';
import { GapVoiceInterviewView } from '../components/resume/GapVoiceInterview';
import { ScenarioTruthPanel } from '../components/ScenarioTruthPanel';
import type { AxisEvidenceScoreRecord, AxisLensReadRecord, ScenarioRecord } from '../lib/api';
import type { SpokenTurn } from '../lib/gapAnswerMode';
import { resolveTalkAvailability } from '../lib/gapAnswerMode';
import { describeScenarioActionError } from '../lib/scenarioTruth';
import { repairTerms } from '../lib/termRepair';
import styles from './MarketingDrafts.module.css';

// H2 FIX-C/D (docs/qa/FIX-PLAN-H2-axisread-starvation-2026-07-10.md) — sample
// AxisLensReadRecord fixtures for the JobFitRadar/AxisLensSignature preview
// states below. Plain fixture data for a style-guide mockup, never served to
// a real user — NO INVENTION governs what the APP shows a real person, not
// what this dev-only preview page renders as a labeled example.
const jobFitFullRead: AxisLensReadRecord = {
  weights: [
    { axisKey: 'people-leadership', weight: 0.82, matchedCues: ['led a team of 6'] },
    { axisKey: 'executive-communication', weight: 0.64, matchedCues: ['board presentation'] },
    { axisKey: 'architecture-systems-design', weight: 0.51, matchedCues: ['system design'] },
    { axisKey: 'data-analysis', weight: 0.3, matchedCues: ['sql'] },
  ],
  register: 'senior',
  coverage: 0.78,
  coverageNote: null,
  taxonomyVersion: 1,
};

const jobFitSparseTwoRead: AxisLensReadRecord = {
  weights: [
    { axisKey: 'people-leadership', weight: 0.7, matchedCues: ['led a team'] },
    { axisKey: 'customer-field', weight: 0.4, matchedCues: ['client-facing'] },
  ],
  register: 'mid',
  coverage: 0.22,
  coverageNote: null,
  taxonomyVersion: 1,
};

const jobFitSparseOneRead: AxisLensReadRecord = {
  weights: [{ axisKey: 'data-analysis', weight: 0.55, matchedCues: ['reporting'] }],
  register: 'early',
  coverage: 0.11,
  coverageNote: null,
  taxonomyVersion: 1,
};

const jobFitEmptyWeightsRead: AxisLensReadRecord = {
  weights: [],
  register: 'mid',
  coverage: 0,
  coverageNote: null,
  taxonomyVersion: 1,
};

// FIX-E part 2 (Lane L-H) — the USER-side evidence read (userScores). Grounded:
// every score > 0 carries at least one real achievement receipt (the server's
// NO-INVENTION gate). "Scored" = the gold polygon fills; "mixed" shows the
// honest 0-receipt "no evidence yet" axes (distinct from an unscored radar's
// "not scored yet"), plotted at the centre because a grounded zero is a real
// value the polygon can honestly draw.
const jobFitEvidenceScored: AxisEvidenceScoreRecord[] = [
  {
    axisKey: 'people-leadership',
    evidence: 0.9,
    receipts: [
      { achievementId: 'a1', matchedText: 'Led a team of 8 across two orgs', matchedTerms: ['leadership', 'people'], affinityScore: 0.9 },
    ],
  },
  {
    axisKey: 'executive-communication',
    evidence: 0.5,
    receipts: [
      { achievementId: 'a2', matchedText: 'Presented quarterly to the exec team', matchedTerms: ['communication'], affinityScore: null },
    ],
  },
  {
    axisKey: 'architecture-systems-design',
    evidence: 0.65,
    receipts: [
      { achievementId: 'a3', matchedText: 'Designed the event-sourced systems core', matchedTerms: ['design', 'systems'], affinityScore: null },
    ],
  },
  {
    axisKey: 'data-analysis',
    evidence: 0.4,
    receipts: [
      { achievementId: 'a4', matchedText: 'Built the SQL analytics pipeline', matchedTerms: ['data'], affinityScore: null },
    ],
  },
];

const jobFitEvidenceMixed: AxisEvidenceScoreRecord[] = [
  {
    axisKey: 'people-leadership',
    evidence: 0.9,
    receipts: [
      { achievementId: 'a1', matchedText: 'Led a team of 8', matchedTerms: ['leadership', 'people'], affinityScore: 0.9 },
    ],
  },
  { axisKey: 'executive-communication', evidence: 0, receipts: [] }, // ran, no receipts → "no evidence yet"
  {
    axisKey: 'architecture-systems-design',
    evidence: 0.65,
    receipts: [
      { achievementId: 'a3', matchedText: 'Designed the systems core', matchedTerms: ['design', 'systems'], affinityScore: null },
    ],
  },
  { axisKey: 'data-analysis', evidence: 0, receipts: [] }, // ran, no receipts → "no evidence yet"
];

const signatureJdTooThinRead: AxisLensReadRecord = {
  weights: [
    { axisKey: 'people-leadership', weight: 0.3, matchedCues: ['led'] },
    { axisKey: 'data-analysis', weight: 0.18, matchedCues: ['data'] },
  ],
  register: 'mid',
  coverage: 0.14,
  coverageNote: 'jd_too_thin',
  taxonomyVersion: 1,
};

const signatureUnstructuredReadRead: AxisLensReadRecord = {
  weights: [
    {
      axisKey: 'executive-communication',
      weight: 0.58,
      matchedCues: ['communicates with stakeholders'],
    },
    { axisKey: 'people-leadership', weight: 0.44, matchedCues: ['mentors junior engineers'] },
    {
      axisKey: 'architecture-systems-design',
      weight: 0.31,
      matchedCues: ['designs scalable systems'],
    },
  ],
  register: 'senior',
  coverage: 0.61,
  coverageNote: 'unstructured_read',
  taxonomyVersion: 1,
};

const signatureEmptyRead: AxisLensReadRecord = {
  weights: [],
  register: 'mid',
  coverage: 0,
  coverageNote: null,
  taxonomyVersion: 1,
};

// =============================================================================
// LANE STORY-TOUR StyleGuide entry — Rule-5 debt closed 2026-07-21
// (docs/WALK-FINDINGS-2026-07-21.md's process notes: "CoachMark had no
// StyleGuide entry"). These are the fixture "stage" CoachMark's own preview
// mounts against, below. CoachMark.module.css's `.root` is `position:
// absolute; inset:0`, sized to its nearest positioned ancestor
// (AppShell.module.css's `.shell` in the real app) — these stages ARE that
// ancestor here, so `position:relative` is load-bearing, not decorative.
// `overflow:hidden` clips the ring's own huge box-shadow spread (the dimming
// "scrim" — CoachMark.module.css's own `.ring` comment) to this box instead
// of letting it visually bleed over the rest of the style guide.
//
// STAGE_STYLE_TALL is sized near viewport height ON PURPOSE:
// CoachMark's resolveCardPlacement compares the target's rect against
// window.innerHeight (the real app's own "shell IS the viewport" mobile-
// first assumption — see that function's own doc comment in CoachMark.tsx),
// so a compact preview box could never genuinely produce the 'above'
// placement no matter where its target sits inside it — only a stage that
// itself approximates viewport height lets the real component resolve
// 'below' vs 'above' honestly, rather than this page faking the outcome.
const STAGE_STYLE_COMPACT: CSSProperties = {
  position: 'relative',
  overflow: 'hidden',
  height: 320,
  maxWidth: 420,
  border: '1px solid var(--tl-line)',
  borderRadius: 16,
  background: 'var(--tl-surf2)',
};

const STAGE_STYLE_TALL: CSSProperties = {
  position: 'relative',
  overflow: 'hidden',
  height: '100vh',
  maxWidth: 420,
  border: '1px solid var(--tl-line)',
  borderRadius: 16,
  background: 'var(--tl-surf2)',
};

/** CoachMark's onAdvance/onSkip are required props (the real host, App.tsx,
 *  always wires real handlers). This static catalog page has no tour STATE
 *  to advance/skip in the first place — every state below is a fixed,
 *  permanent fixture, the same "just render each state" idiom every other
 *  entry in this file already uses — so both props get this one harmless
 *  no-op rather than the page inventing tour state machinery of its own
 *  just to satisfy the type. */
function noopTourHandler(): void {}

/** ScenarioTruthPanel's onRetire/onReactivate are required props; this catalog
 *  page has no server to call, so both get the same harmless no-op the tour
 *  handlers above use rather than the page inventing a fake write path. */
function noopScenarioAction(): void {}

// ─────────────────────────────────────────────────────────────────────────
// TALK-IT-THROUGH fixtures (L-M / W22, rule 5). The transcript below is the
// walk's OWN moment: Meira's real gap question and the mishearing his dictated
// answer actually came back with ("multi Khloud", "Cooper Berti's"), so the
// ready-to-confirm state previews the repair pass against the case that
// prompted the lane rather than invented sample text.
//
// Note the shape of the fixture: Meira's line is present in `turns` (it
// renders) and absent from everything the confirm action can send — that
// separation is the whole point of the state, so the preview shows it.
// ─────────────────────────────────────────────────────────────────────────
const voiceAvailable = resolveTalkAvailability({ hasMediaDevices: true, isSecureContext: true });
const voiceUnsupported = resolveTalkAvailability({
  hasMediaDevices: false,
  isSecureContext: true,
});

const voiceTurns: SpokenTurn[] = [
  {
    role: 'agent',
    text: "So what's actually keeping you tied to the States right now? Is it visa, family, something else?",
  },
  { role: 'user', text: "Family mostly. My kid's mid-year at school and we're not moving her." },
];

const voiceHeardAnswer =
  "We ran multi Khloud environments with Cooper Berti's to track the workloads.";
const voiceRepair = repairTerms(voiceHeardAnswer);
const voiceCorrections = voiceRepair.corrections;
const voiceTurnsRepaired: SpokenTurn[] = [
  ...voiceTurns,
  { role: 'agent', text: 'And on the platform side, what were you actually running?' },
  { role: 'user', text: voiceRepair.corrected },
];

/** GapVoiceInterviewView's five handlers are required props; this catalog page
 *  has no live session, so they get the same harmless no-op the handlers above
 *  use rather than the page faking a transport. */
const voiceNoops = {
  onStart: noopTourHandler,
  onStop: noopTourHandler,
  onUseAnswer: noopTourHandler,
  onRevertCorrection: noopTourHandler,
  onRetry: noopTourHandler,
};

// ─────────────────────────────────────────────────────────────────────────
// SCENARIO TRUTH PANEL fixtures (L-K, rule 5). Hand-written stand-ins for a
// GET /api/scenarios row — shaped exactly like the served payload (the whole
// search_scenarios row plus R386's derived retiredStale/staleReason/
// staleDetail). The two retired fixtures are the two REAL reason codes
// apps/api/lib/scenarioStaleness.ts emits, with that module's own detail
// sentences, so the preview shows what the server actually says rather than
// invented copy.
// ─────────────────────────────────────────────────────────────────────────

const scenarioLive: ScenarioRecord = {
  id: 'sg-scenario-live',
  name: 'Stateside',
  active: true,
  isPrimary: true,
  locations: ['Fort Lauderdale', 'Miami', 'Remote US'],
  workAuthorization: 'us_authorized',
  remoteOk: true,
  retiredAt: null,
  retiredReason: null,
  retiredStale: false,
  staleReason: null,
  staleDetail: null,
};

/** DERIVED-stale: no retire stamp, so there is nothing for POST /reactivate to
 *  clear — the panel offers the honest route back instead of a button that
 *  would do nothing. */
const scenarioRetiredDerived: ScenarioRecord = {
  id: 'sg-scenario-derived',
  name: 'Overseas move',
  active: true,
  isPrimary: false,
  locations: ['Brisbane', 'Sydney', 'Remote AU'],
  workAuthorization: 'au_partner_visa',
  remoteOk: true,
  retiredAt: null,
  retiredReason: null,
  retiredStale: true,
  staleReason: 'contradicts_home_no_declared_intent',
  staleDetail:
    'Targets AU on a visa path while your home base is US and no move to AU is declared.',
};

/** USER-retired: a real stamp exists, so "Bring it back" genuinely works. */
const scenarioRetiredByUser: ScenarioRecord = {
  id: 'sg-scenario-user-retired',
  name: 'London',
  active: true,
  isPrimary: false,
  locations: ['London'],
  workAuthorization: 'other',
  remoteOk: false,
  retiredAt: '2026-07-21T14:02:00.000Z',
  retiredReason: 'Not moving after all.',
  retiredStale: true,
  staleReason: 'user_retired',
  staleDetail: 'You retired this scenario.',
};

/**
 * Throughline style guide — CLAUDE.md hard rule 5 ("build a style guide
 * preview entry first... before it gets wired into a real screen"). This app
 * had no style guide file before WO-3 (verified: grep for "StyleGuide"
 * across apps/throughline/src returned nothing); apps/web/src/pages/
 * StyleGuide.tsx is a SEPARATE app with its own design system (Tailwind) —
 * importing a throughline CSS-module component there would not render
 * correctly, so this is a throughline-native style guide, seeded honestly
 * with only the ONE component this stream added (the marketing-drafts
 * review card). Not wired into the tab bar (an operator/dev surface); reach
 * it directly at /style-guide.
 *
 * States shown, per the house rule's checklist: idle (populated), loading,
 * error, empty. "drag-source"/"drag-target"/"over-budget" do not apply to
 * this component (no drag, no budget meter) — omitted rather than faked.
 */
export function StyleGuide() {
  const sampleDraft = {
    id: 'sample-1',
    kind: 'digest',
    title: 'This week: 12 new roles, 3 applications tracked',
    body: 'A short first-person digest paragraph would render here — grounded only in real, queried counts, never invented.',
    sourceRefs: [
      { kind: 'new_job_listings_count', description: 'New job listings discovered', value: 12 },
      { kind: 'applications_status_applied', description: 'Applications currently in status "applied"', value: 3 },
    ],
  };

  return (
    <div className={styles.page}>
      <div className={styles.eyebrow}>
        <div className={styles.kicker}>Dev</div>
        <div className={styles.title}>Style guide</div>
      </div>
      <p className={styles.subtitle}>Every state, before it ships into a real screen.</p>

      <h3 style={{ margin: '18px 0 8px' }}>Marketing draft card — idle</h3>
      <Card className={styles.draftCard}>
        <div className={styles.draftKind}>{sampleDraft.kind}</div>
        <div className={styles.draftTitle}>{sampleDraft.title}</div>
        <div className={styles.draftBody}>{sampleDraft.body}</div>
        <div className={styles.sourceRefs}>
          {sampleDraft.sourceRefs.map((ref, i) => (
            <span key={i} className={styles.sourceRefChip}>
              {ref.description}: {ref.value}
            </span>
          ))}
        </div>
        <div className={styles.actions}>
          <button type="button" className={styles.approveButton}>
            Approve
          </button>
          <button type="button" className={styles.rejectButton}>
            Reject
          </button>
        </div>
      </Card>

      <h3 style={{ margin: '18px 0 8px' }}>Marketing draft card — acting (disabled)</h3>
      <Card className={styles.draftCard}>
        <div className={styles.draftKind}>{sampleDraft.kind}</div>
        <div className={styles.draftTitle}>{sampleDraft.title}</div>
        <div className={styles.draftBody}>{sampleDraft.body}</div>
        <div className={styles.actions}>
          <button type="button" className={styles.approveButton} disabled>
            Approve
          </button>
          <button type="button" className={styles.rejectButton} disabled>
            Reject
          </button>
        </div>
      </Card>

      <h3 style={{ margin: '18px 0 8px' }}>Loading</h3>
      <div className={styles.status}>Loading drafts…</div>

      <h3 style={{ margin: '18px 0 8px' }}>Error</h3>
      <div className={styles.error}>Could not load drafts.</div>

      <h3 style={{ margin: '18px 0 8px' }}>Empty</h3>
      <div className={styles.status}>No drafts waiting on review.</div>

      <h3 style={{ margin: '32px 0 8px' }}>
        Loop offer chip — Phase 1, fix architecture 2026-07-08 (Class 6, GET
        /api/loop/offers)
      </h3>
      <p className={styles.subtitle}>
        depthDelta is real, countable arithmetic (facets landed, gaps closed, corrections
        confirmed) read from evaluateRegen — never a generated sentence. Not wired into a real
        screen yet (Phase 1 ships the backend primitive; Phase 2/3 wire the FE consumer).
      </p>

      <div className={styles.draftKind}>offer — idle</div>
      <Card className={styles.draftCard}>
        <div className={styles.draftKind}>resume</div>
        <div className={styles.draftTitle}>Quotient Health — Senior Platform Engineer</div>
        <div className={styles.draftBody}>This version can deepen with what you've added.</div>
        <div className={styles.sourceRefs}>
          <span className={styles.sourceRefChip}>+4 facets landed</span>
          <span className={styles.sourceRefChip}>2 gaps closed</span>
          <span className={styles.sourceRefChip}>1 correction confirmed</span>
        </div>
        <div className={styles.actions}>
          <button type="button" className={styles.approveButton}>
            Regenerate
          </button>
          <button type="button" className={styles.rejectButton}>
            Not now
          </button>
        </div>
      </Card>

      <div className={styles.draftKind}>offer — acting (disabled)</div>
      <Card className={styles.draftCard}>
        <div className={styles.draftKind}>cover_letter</div>
        <div className={styles.draftTitle}>Aurizon — Data & Analytics Lead</div>
        <div className={styles.draftBody}>This version can deepen with what you've added.</div>
        <div className={styles.actions}>
          <button type="button" className={styles.approveButton} disabled>
            Regenerate
          </button>
          <button type="button" className={styles.rejectButton} disabled>
            Not now
          </button>
        </div>
      </Card>

      <div className={styles.draftKind}>offer — loading</div>
      <div className={styles.status}>Checking what's changed…</div>

      <div className={styles.draftKind}>offer — error</div>
      <div className={styles.error}>Could not check for updates.</div>

      <div className={styles.draftKind}>offer — empty (nothing to offer this epoch)</div>
      <div className={styles.status}>Everything's current — no new versions to offer.</div>

      <h3 style={{ margin: '32px 0 8px' }}>
        Closing-state chip — Phase 1, fix architecture 2026-07-08 (D3, GET /corpus/loop/state's
        openCount finally consumed)
      </h3>
      <p className={styles.subtitle}>
        Named states only — never a bare count with no meaning. "Settled" also feeds the D16
        forward-motion framing in the interview voice (see keystoneHubGrounding).
      </p>

      <div className={styles.draftKind}>closing-state — open (eras still want probing)</div>
      <div className={`${styles.closingChip} ${styles.closingChipOpen}`}>
        {'●'} 6 topics still open
      </div>

      <div className={styles.draftKind}>closing-state — settling (getting close, tunable bar)</div>
      <div className={`${styles.closingChip} ${styles.closingChipSettling}`}>
        {'●'} Almost there — 1 topic left
      </div>

      <div className={styles.draftKind}>closing-state — settled (D16's forward-motion moment)</div>
      <div className={`${styles.closingChip} ${styles.closingChipSettled}`}>
        {'●'} We've covered it all — ready to strengthen
      </div>

      <div className={styles.draftKind}>closing-state — loading</div>
      <div className={styles.status}>Reading where things stand…</div>

      <div className={styles.draftKind}>closing-state — error</div>
      <div className={styles.error}>Could not read the loop's state.</div>

      <div className={styles.draftKind}>closing-state — empty (nothing compiled yet, not "settled")</div>
      <div className={styles.status}>Nothing tracked yet — this fills in as we talk.</div>

      <h3 style={{ margin: '32px 0 8px' }}>
        Claim correction control — Phase 2 Lane B, fix architecture 2026-07-08 (D12 + the job-side
        half of D11, POST/GET /corpus/claim-corrections); MIC IDIOM + UNDO added Phase C/C-P1
        (docs/PLAN-MASTER-2026-07-09.md)
      </h3>
      <p className={styles.subtitle}>
        The "that's not right" affordance. Generalized from AlignmentReviewCard's Move/Keep/
        Flag-split/Merge pattern. NEVER rewrites the claim text — only adds an honest
        strikethrough/chip once a ruling lands. Wired onto the job detail "why this fit" panel's
        gap + strengths bullets (Phase 2 Lane B) and the alignment essay panel's flagged claims +
        whole-essay note (Phase C/C-P1) — the radar/through-line surfaces follow the same
        component. The note field is a VoiceTextarea (rule 2 — mic-capable, type or speak it).
        "drag-source"/"drag-target"/"over-budget" do not apply to this component (no drag, no
        budget meter) — omitted rather than faked.
      </p>

      <div className={styles.draftKind}>idle</div>
      <Card className={styles.draftCard}>
        <div className={styles.draftBody}>No experience with Kubernetes</div>
        <ClaimCorrectionControl
          sourceType="gap"
          sourceId="00000000-0000-0000-0000-000000000001"
          flaggedText="No experience with Kubernetes"
        />
      </Card>

      <div className={styles.draftKind}>open (note input — mic-capable, rule 2)</div>
      <Card className={styles.draftCard}>
        <div className={styles.draftBody}>No public speaking experience</div>
        <div style={{ marginTop: 6, display: 'flex', flexDirection: 'column', gap: 6, maxWidth: 360 }}>
          <div>
            <div className={styles.matchNote} style={{ marginBottom: 4 }}>
              What's actually true here? (optional)
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <div
                style={{
                  flex: '1 1 200px',
                  fontSize: 13,
                  padding: '6px 8px',
                  borderRadius: 6,
                  border: '1px solid var(--tl-line)',
                  background: 'var(--tl-surf)',
                  color: 'var(--tl-ink-soft)',
                }}
              >
                Type it, or tap the mic and say it…
              </div>
              {/* The mic button idiom (VoiceTextarea.tsx) — a static preview
                  of its idle glyph, since this block is a disabled mockup
                  (the LIVE control above already exercises the real
                  VoiceTextarea in its 'idle' Card). */}
              <div
                aria-hidden="true"
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: 28,
                  height: 28,
                  borderRadius: 999,
                  border: '1px solid var(--tl-line)',
                  color: 'var(--tl-ink-soft)',
                }}
              >
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                  <rect x="9" y="3" width="6" height="11" rx="3" stroke="currentColor" strokeWidth="1.9" />
                  <path d="M5 11a7 7 0 0 0 14 0M12 18v3" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" />
                </svg>
              </div>
            </div>
          </div>
          <div style={{ display: 'flex', gap: 6 }}>
            <button
              type="button"
              disabled
              style={{
                fontSize: 12,
                fontWeight: 600,
                padding: '6px 10px',
                borderRadius: 6,
                border: '1px solid var(--tl-accent)',
                background: 'var(--tl-accent-soft)',
                color: 'var(--tl-accent-ink)',
              }}
            >
              Save
            </button>
            <button
              type="button"
              disabled
              style={{
                fontSize: 12,
                fontWeight: 500,
                padding: '6px 10px',
                borderRadius: 6,
                border: '1px solid var(--tl-line)',
                background: 'transparent',
                color: 'var(--tl-ink-soft)',
              }}
            >
              Cancel
            </button>
          </div>
        </div>
      </Card>

      <div className={styles.draftKind}>corrected (terminal — strikethrough + chip + undo)</div>
      <Card className={styles.draftCard}>
        <ClaimCorrectionControl
          sourceType="gap"
          sourceId="00000000-0000-0000-0000-000000000002"
          flaggedText="No leadership experience"
          existingCorrection={{
            id: 'preview-1',
            sourceType: 'gap',
            sourceId: '00000000-0000-0000-0000-000000000002',
            flaggedText: 'No leadership experience',
            userNote: 'I led a team of 6 for 2 years at my last role',
            ruling: 'disputed',
            createdAt: new Date().toISOString(),
          }}
        />
      </Card>

      <div className={styles.draftKind}>error (save failed, inline retry)</div>
      <div className={styles.error}>Couldn't save that — try again.</div>

      <h3 style={{ margin: '32px 0 8px' }}>
        Declared-input banner — Phase 2a, fix architecture 2026-07-08 (D19/D17, THE
        DECLARED-INPUTS ASK)
      </h3>
      <p className={styles.subtitle}>
        Renders BEFORE the Jobs board's search/results, exactly when GET /jobs's served
        declaredInputs.missingInputs is non-empty — never a client-derived guess. No
        "dismiss forever": continually reinforced per his own wording, not a nag counter.
      </p>

      <div className={styles.draftKind}>banner — idle (missing input)</div>
      <DeclaredInputBanner
        missingInputs={['home_country', 'home_postal_code']}
        declareState={{ status: 'idle', error: null }}
        onDeclare={() => {}}
      />

      <div className={styles.draftKind}>banner — saving</div>
      <DeclaredInputBanner
        missingInputs={['home_country', 'home_postal_code']}
        declareState={{ status: 'saving', error: null }}
        onDeclare={() => {}}
      />

      <div className={styles.draftKind}>banner — error</div>
      <DeclaredInputBanner
        missingInputs={['home_country', 'home_postal_code']}
        declareState={{ status: 'error', error: "Couldn't save that — try again." }}
        onDeclare={() => {}}
      />

      <div className={styles.draftKind}>banner — satisfied (renders nothing, the honest empty state)</div>
      <div className={styles.status}>
        (nothing renders here — DeclaredInputBanner returns null when missingInputs is empty)
      </div>

      <h3 style={{ margin: '32px 0 8px' }}>
        Sort-criteria transparency line — Phase 2a (D19b, BucketFilterChips)
      </h3>
      <p className={styles.subtitle}>
        "sorted on: ..." — GET /jobs's own served declaredInputs.sortCriteria string, never a
        client-invented label.
      </p>
      <div className={bannerStyles.banner} style={{ background: 'none', padding: 0 }}>
        <div style={{ fontSize: 11, color: 'var(--tl-ink-soft)' }}>
          sorted on: home base (not set) · fit · recency
        </div>
      </div>

      <h3 style={{ margin: '32px 0 8px' }}>
        Honest score-confidence chip — Phase 2a (D21, THE RESCORING ENGINE)
      </h3>
      <p className={styles.subtitle}>
        "Instead of saying 'I don't know' it should say 'I DO know — this is what I know SO
        FAR.'" Never a number ring for a provisional/not-yet-attempted job — a chip naming the
        known facets instead.
      </p>

      <div className={styles.draftKind}>score chip — provisional (some facets known)</div>
      <div
        style={{
          display: 'inline-block',
          maxWidth: 88,
          padding: '6px 8px',
          borderRadius: 12,
          background: 'var(--tl-accent-soft)',
          color: 'var(--tl-accent)',
          fontSize: 10.5,
          fontWeight: 600,
          textAlign: 'center',
        }}
      >
        I know so far: the role title, your home base
      </div>

      <div className={styles.draftKind} style={{ marginTop: 12 }}>
        score chip — not yet attempted (nothing known)
      </div>
      <div
        style={{
          display: 'inline-block',
          maxWidth: 88,
          padding: '6px 8px',
          borderRadius: 12,
          background: 'var(--tl-accent-soft)',
          color: 'var(--tl-accent)',
          fontSize: 10.5,
          fontWeight: 600,
          textAlign: 'center',
        }}
      >
        Not enough to score yet
      </div>

      <h3 style={{ margin: '32px 0 8px' }}>
        Explore — not fully scored shelf — Phase 2a (D21 thread 3)
      </h3>
      <p className={styles.subtitle}>
        A separate dimension from the six-bucket lane — a job can be bucket='home' AND still
        provisional. Collapsed by default, out of the way of the main flow.
      </p>
      <div className={styles.draftKind}>shelf — collapsed</div>
      <button
        type="button"
        style={{
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          background: 'none',
          border: 'none',
          fontFamily: 'inherit',
          fontSize: 13,
          fontWeight: 600,
          color: 'var(--tl-ink-soft)',
          padding: '6px 0',
          cursor: 'pointer',
        }}
      >
        Explore — not fully scored (3)
        <span>▾</span>
      </button>

      <h3 style={{ margin: '32px 0 8px' }}>
        Job-fit radar — H2 FIX-C, the render pin
        (docs/qa/FIX-PLAN-H2-axisread-starvation-2026-07-10.md)
      </h3>
      <p className={styles.subtitle}>
        "render-sparse-never-remove" — the ring+spoke scaffold now draws in every state; only the
        closed data polygon needs 3+ real axes. A lake-ingested job with a rich description but no
        extracted requirement bullets used to read as a bare text line below 3 traced axes — the
        scaffold never vanishes now, sparse is shown honestly as sparse, never padded with a
        fabricated third point.
      </p>

      <div className={styles.draftKind}>radar — full (3+ traced axes, today's web, unchanged)</div>
      <Card className={styles.draftCard}>
        <JobFitRadar axisRead={jobFitFullRead} />
      </Card>

      <div className={styles.draftKind}>
        radar — sparse (2 traced axes, no fabricated third point)
      </div>
      <Card className={styles.draftCard}>
        <JobFitRadar axisRead={jobFitSparseTwoRead} />
      </Card>

      <div className={styles.draftKind}>radar — sparse (1 traced axis)</div>
      <Card className={styles.draftCard}>
        <JobFitRadar axisRead={jobFitSparseOneRead} />
      </Card>

      <div className={styles.draftKind}>radar — empty (axis read resolved, nothing traced)</div>
      <Card className={styles.draftCard}>
        <JobFitRadar axisRead={jobFitEmptyWeightsRead} />
      </Card>

      <div className={styles.draftKind}>radar — empty (no axis read yet, axisRead === null)</div>
      <Card className={styles.draftCard}>
        <JobFitRadar axisRead={null} />
      </Card>

      <h3 style={{ margin: '32px 0 8px' }}>
        Job-fit radar — FIX-E part 2, the USER evidence half (Lane L-H,
        docs/design/evidence-scoring-2026-07-24.md)
      </h3>
      <p className={styles.subtitle}>
        The owner's walk: "the score of my experience doesn't have any overlap." R383 built the
        role side; this fills the gold polygon from his REAL corpus, grounded — every scored axis
        carries at least one achievement receipt (hover a legend row to see them). Three honest
        states per axis: <strong>N%</strong> (receipt-backed) · <strong>no evidence yet</strong>
        (scored, zero receipts — a real grounded zero) · <strong>not scored yet</strong> (the
        scorer didn't run). The null-vs-zero split is the no-invention line.
      </p>

      <div className={styles.draftKind}>
        radar — evidence scored (gold polygon fills; receipts on legend-row hover)
      </div>
      <Card className={styles.draftCard}>
        <JobFitRadar axisRead={jobFitFullRead} evidenceScores={jobFitEvidenceScored} />
      </Card>

      <div className={styles.draftKind}>
        radar — mixed evidence ("no evidence yet" on the ungrounded axes, distinct from "not scored
        yet")
      </div>
      <Card className={styles.draftCard}>
        <JobFitRadar axisRead={jobFitFullRead} evidenceScores={jobFitEvidenceMixed} />
      </Card>

      <h3 style={{ margin: '32px 0 8px' }}>
        Axis-lens signature — H2 FIX-D, one composed thin state (AxisLensCard.tsx)
      </h3>
      <p className={styles.subtitle}>
        Replaces the old three-stacked-fragments render (a filled register pill + a floating italic
        tag + a separate bare empty line, three visual languages) with ONE contained block for
        exactly three named cases. A normal, confidently-covered read — and reads_unusual with bars
        present — is untouched by this fix, not shown again here.
      </p>

      <div className={styles.draftKind}>signature — jd_too_thin (too thin to read fully)</div>
      <Card className={styles.draftCard}>
        <AxisLensSignature read={signatureJdTooThinRead} />
      </Card>

      <div className={styles.draftKind}>
        signature — unstructured_read (Lane 1's JD-source seam: real traced weights, read from the
        posting's description rather than extracted requirement bullets)
      </div>
      <Card className={styles.draftCard}>
        <AxisLensSignature read={signatureUnstructuredReadRead} />
      </Card>

      <div className={styles.draftKind}>
        signature — empty (resolved, nothing traced, no specific note)
      </div>
      <Card className={styles.draftCard}>
        <AxisLensSignature read={signatureEmptyRead} />
      </Card>

      <h3 style={{ margin: '32px 0 8px' }}>
        E7 front door — the 7-card guided first-login flow (LANE
        E7-PORT-THROUGHLINE, docs/diagrams/c4-e7-front-door-2026-07-17.html)
      </h3>
      <p className={styles.subtitle}>
        Ported from apps/web's reference build, composed as the leading segment of the existing
        onboarding arc (App.tsx). Every card below is the REAL component, mock props only — no
        backend call fires from rendering this page. States shown per card: idle / suggested (cards
        1-2 only, the ones the server sends a live suggestion for) / confirmed / skipped. "error" is
        ONE shared banner mechanism (FrontDoorCards.tsx's saveError), not styled differently per
        card — shown once below, not repeated seven times. Card 4's Continue button starts DISABLED
        at zero selections (visible in its own idle state below) — an empty-array save would poison
        the citizenship-derived work-rights default via overrides; skip is the only honest zero-
        selection path.
      </p>
      <p className={styles.subtitle}>
        E7-WALK-WAVE (his live 3-screenshot test, 2026-07-17): Card 4 gained the "No others — home
        country only" declared-none chip (W2 — a distinct attributes.workRightsNone flag, never
        workRights: [], see frontDoorCardBodies.tsx's own doc comment on the poison it avoids). Card
        5 changed from single-select to MULTI-select (W1 — local AND domestic AND international can
        all be true at once, attributes.relocationPostures). Card 7's ImportPasteCard now renders
        with a visibly larger paste target + "paste in pieces is fine" reassurance copy (W4) —
        visible below in both finale states, no separate demo needed since FinaleCard always passes
        those props now.
      </p>

      <h4 style={{ margin: '20px 0 6px' }}>Card 1 — preferred name</h4>
      <div className={styles.draftKind}>idle (no suggestion yet)</div>
      <Card className={styles.draftCard}>
        <StepFrame title="What should Meira call you?" onSkip={() => {}}>
          <PreferredNameCard value={null} suggestion={null} onSave={() => {}} />
        </StepFrame>
      </Card>
      <div className={styles.draftKind}>suggested (OAuth display name's first token — unconfirmed)</div>
      <Card className={styles.draftCard}>
        <StepFrame title="What should Meira call you?" onSkip={() => {}}>
          <PreferredNameCard value={null} suggestion="Jason" onSave={() => {}} />
        </StepFrame>
      </Card>
      <div className={styles.draftKind}>confirmed</div>
      <Card className={styles.draftCard}>
        <StepFrame title="What should Meira call you?" onSkip={() => {}}>
          <PreferredNameCard value="Jay" suggestion={null} onSave={() => {}} />
        </StepFrame>
      </Card>
      <div className={styles.draftKind}>skipped (backed into after a prior skip, still unanswered)</div>
      <Card className={styles.draftCard}>
        <StepFrame title="What should Meira call you?" skippedBadge onSkip={() => {}}>
          <PreferredNameCard value={null} suggestion={null} onSave={() => {}} />
        </StepFrame>
      </Card>

      <h4 style={{ margin: '20px 0 6px' }}>Card 2 — confirm home country</h4>
      <div className={styles.draftKind}>idle</div>
      <Card className={styles.draftCard}>
        <StepFrame
          title="Confirm your home country"
          subtitle="This sets the honest default for spelling, résumé format, and other conventions."
          onSkip={() => {}}
        >
          <HomeCountryCard value={null} suggestion={null} onSave={() => {}} />
        </StepFrame>
      </Card>
      <div className={styles.draftKind}>
        suggested (Cloudflare geo hint — visually distinct until his own tap confirms it)
      </div>
      <Card className={styles.draftCard}>
        <StepFrame
          title="Confirm your home country"
          subtitle="This sets the honest default for spelling, résumé format, and other conventions."
          onSkip={() => {}}
        >
          <HomeCountryCard value={null} suggestion="US" onSave={() => {}} />
        </StepFrame>
      </Card>
      <div className={styles.draftKind}>confirmed</div>
      <Card className={styles.draftCard}>
        <StepFrame
          title="Confirm your home country"
          subtitle="This sets the honest default for spelling, résumé format, and other conventions."
          onSkip={() => {}}
        >
          <HomeCountryCard value="AU" suggestion="US" onSave={() => {}} />
        </StepFrame>
      </Card>
      <div className={styles.draftKind}>skipped</div>
      <Card className={styles.draftCard}>
        <StepFrame
          title="Confirm your home country"
          subtitle="This sets the honest default for spelling, résumé format, and other conventions."
          skippedBadge
          onSkip={() => {}}
        >
          <HomeCountryCard value={null} suggestion={null} onSave={() => {}} />
        </StepFrame>
      </Card>

      <h4 style={{ margin: '20px 0 6px' }}>Card 3 — post code + radius</h4>
      <div className={styles.draftKind}>idle</div>
      <Card className={styles.draftCard}>
        <StepFrame
          title="Where's your home base?"
          subtitle="Lets us keep local and 100%-remote roles within reach."
          onSkip={() => {}}
        >
          <PostalRadiusCard postalCode={null} radiusMiles={null} onSave={() => {}} />
        </StepFrame>
      </Card>
      <div className={styles.draftKind}>confirmed</div>
      <Card className={styles.draftCard}>
        <StepFrame
          title="Where's your home base?"
          subtitle="Lets us keep local and 100%-remote roles within reach."
          onSkip={() => {}}
        >
          <PostalRadiusCard postalCode="33139" radiusMiles={50} onSave={() => {}} />
        </StepFrame>
      </Card>
      <div className={styles.draftKind}>skipped</div>
      <Card className={styles.draftCard}>
        <StepFrame
          title="Where's your home base?"
          subtitle="Lets us keep local and 100%-remote roles within reach."
          skippedBadge
          onSkip={() => {}}
        >
          <PostalRadiusCard postalCode={null} radiusMiles={null} onSave={() => {}} />
        </StepFrame>
      </Card>

      <h4 style={{ margin: '20px 0 6px' }}>Card 4 — work rights elsewhere</h4>
      <div className={styles.draftKind}>
        idle (0 selected, none declared — Continue disabled by design, see caption above)
      </div>
      <Card className={styles.draftCard}>
        <StepFrame title="Work rights anywhere else?" onSkip={() => {}}>
          <WorkRightsCard selected={[]} noneDeclared={false} onSave={() => {}} />
        </StepFrame>
      </Card>
      <div className={styles.draftKind}>confirmed (2 selected)</div>
      <Card className={styles.draftCard}>
        <StepFrame title="Work rights anywhere else?" onSkip={() => {}}>
          <WorkRightsCard selected={['US', 'AU']} noneDeclared={false} onSave={() => {}} />
        </StepFrame>
      </Card>
      <div className={styles.draftKind}>
        confirmed — "No others" declared (W2, 2026-07-17: the missing honest answer his live test
        flagged; distinct from idle, never workRights: [])
      </div>
      <Card className={styles.draftCard}>
        <StepFrame title="Work rights anywhere else?" onSkip={() => {}}>
          <WorkRightsCard selected={[]} noneDeclared onSave={() => {}} />
        </StepFrame>
      </Card>
      <div className={styles.draftKind}>skipped</div>
      <Card className={styles.draftCard}>
        <StepFrame title="Work rights anywhere else?" skippedBadge onSkip={() => {}}>
          <WorkRightsCard selected={[]} noneDeclared={false} onSave={() => {}} />
        </StepFrame>
      </Card>

      <h4 style={{ margin: '20px 0 6px' }}>Card 5 — open to relocation</h4>
      <div className={styles.draftKind}>idle</div>
      <Card className={styles.draftCard}>
        <StepFrame title="Open to relocation?" onSkip={() => {}}>
          <RelocationCard postures={[]} targets={[]} onSave={() => {}} />
        </StepFrame>
      </Card>
      <div className={styles.draftKind}>confirmed — single posture (stay local only)</div>
      <Card className={styles.draftCard}>
        <StepFrame title="Open to relocation?" onSkip={() => {}}>
          <RelocationCard postures={['stay_local']} targets={[]} onSave={() => {}} />
        </StepFrame>
      </Card>
      <div className={styles.draftKind}>
        confirmed — MULTI-select (W1, 2026-07-17: local AND domestic AND international all at once,
        his requirement — the whole point of the fix), with declared targets
      </div>
      <Card className={styles.draftCard}>
        <StepFrame title="Open to relocation?" onSkip={() => {}}>
          <RelocationCard
            postures={['stay_local', 'domestic', 'international']}
            targets={['Austin', 'Denver']}
            onSave={() => {}}
          />
        </StepFrame>
      </Card>
      <div className={styles.draftKind}>skipped</div>
      <Card className={styles.draftCard}>
        <StepFrame title="Open to relocation?" skippedBadge onSkip={() => {}}>
          <RelocationCard postures={[]} targets={[]} onSave={() => {}} />
        </StepFrame>
      </Card>

      <h4 style={{ margin: '20px 0 6px' }}>Card 6 — email (read-only) + phone</h4>
      <div className={styles.draftKind}>idle (no phone declared)</div>
      <Card className={styles.draftCard}>
        <StepFrame title="How do we reach you?" onSkip={() => {}}>
          <ContactCard email="jason@example.com" phone={null} onSave={() => {}} />
        </StepFrame>
      </Card>
      <div className={styles.draftKind}>confirmed (phone declared)</div>
      <Card className={styles.draftCard}>
        <StepFrame title="How do we reach you?" onSkip={() => {}}>
          <ContactCard email="jason@example.com" phone="+1 555 0100" onSave={() => {}} />
        </StepFrame>
      </Card>
      <div className={styles.draftKind}>skipped</div>
      <Card className={styles.draftCard}>
        <StepFrame title="How do we reach you?" skippedBadge onSkip={() => {}}>
          <ContactCard email="jason@example.com" phone={null} onSave={() => {}} />
        </StepFrame>
      </Card>

      <h4 style={{ margin: '20px 0 6px' }}>Card 7 — the finale (seed the corpus)</h4>
      <p className={styles.subtitle}>
        Composes the EXISTING corpus-entry door (ImportPasteCard, the same component the Refine tab
        and the existing arc's one-question step already use) rather than a second upload surface —
        "the finale card's upload/paste routes into the arc's own corpus entry." That component owns
        its own paste/upload sub-states internally (no props to force them independently), so this
        page shows the finale's own two real states: empty (before any import) and seeded (after).
      </p>
      <div className={styles.draftKind}>empty (before any import — the live ImportPasteCard, no backend call fires by rendering it)</div>
      <Card className={styles.draftCard}>
        <StepFrame title="Let's seed your corpus" onSkip={() => {}} skipLabel="Skip — I’ll add this later">
          <FinaleCard seeded={false} onImported={() => {}} onFinish={() => {}} />
        </StepFrame>
      </Card>
      <div className={styles.draftKind}>seeded (Meira's promise — verbatim)</div>
      <Card className={styles.draftCard}>
        <StepFrame title="Let's seed your corpus" onSkip={() => {}} skipLabel="Skip — I’ll add this later">
          <FinaleCard seeded resultNote="Found 2 roles and 3 stories." onImported={() => {}} onFinish={() => {}} />
        </StepFrame>
      </Card>

      <h4 style={{ margin: '20px 0 6px' }}>Error — the shared save-error banner</h4>
      <p className={styles.subtitle}>
        One mechanism, not styled per card (FrontDoorCards.tsx's own saveError, rendered once below
        whichever card is showing).
      </p>
      <Card className={styles.draftCard}>
        <StepFrame title="What should Meira call you?" onSkip={() => {}}>
          <PreferredNameCard value={null} suggestion={null} onSave={() => {}} />
        </StepFrame>
        <div className={frontDoorStyles.errorRow} role="alert">
          Couldn’t save that: network error
        </div>
      </Card>

      <div className={styles.draftKind} style={{ marginTop: 16 }}>
        Preview banner (?onboarding=preview — his test path, badge copy verbatim)
      </div>
      <Card className={styles.draftCard}>
        <div className={frontDoorStyles.previewBanner}>
          Preview — your answers save to your profile; the first-login gate is not stamped.
        </div>
      </Card>

      <h3 style={{ margin: '32px 0 8px' }}>
        Coach mark — LANE STORY-TOUR (components/CoachMark.tsx)
      </h3>
      <p className={styles.subtitle}>
        Rule #5 debt from the original story-tour lane (docs/WALK-FINDINGS-2026-07-21.md's
        process notes: "CoachMark had no StyleGuide entry"). Every stage below mounts the REAL
        component against a fixture target/stage — never a mock — so the actual anchor-resolution,
        measure, and scroll-into-view effects fire exactly as they do live. Two consequences of
        that honesty, worth knowing before you scroll further: (1) the "anchored" stages are built
        tall on purpose — CoachMark's own placement rule (resolveCardPlacement) compares the
        target's position against window.innerHeight, the same "shell IS the viewport" assumption
        the real phone-frame shell satisfies, so a short fixture box can never genuinely produce
        the "above" placement; and (2) an anchored target that starts off-screen triggers the
        real component's real "scroll it into view" reveal (his 2026-07-20 walk fix) — expect the
        page to jump once on load. That is the fix working, not a bug in this preview.
      </p>

      <div className={styles.draftKind}>
        welcome — first paint, centered (no anchor), its own "I'll explore on my own" secondary
        label (every other step shares the plain "Skip tour" label — see below)
      </div>
      <div style={STAGE_STYLE_COMPACT}>
        <CoachMark step="welcome" currentPath="/" onAdvance={noopTourHandler} onSkip={noopTourHandler} />
      </div>

      <div className={styles.draftKind}>
        home-orientation — anchored BELOW a top-half target (his 2026-07-20 walk: "needs to show
        and highlight the spot" + the card rides WITH the ring, just under it)
      </div>
      <div style={STAGE_STYLE_TALL}>
        <Card tourAnchor="home-story-score" className={styles.draftCard}>
          Story-score card (fixture target — real anchor id "home-story-score", Home.tsx)
        </Card>
        <CoachMark
          step="home-orientation"
          currentPath="/"
          onAdvance={noopTourHandler}
          onSkip={noopTourHandler}
        />
      </div>

      <div className={styles.draftKind}>
        first-story — anchored ABOVE a bottom-half target (mic button near the foot of the
        screen — the card seats itself over the ring rather than running off the bottom edge)
      </div>
      <div style={STAGE_STYLE_TALL}>
        <div style={{ height: '65vh' }} aria-hidden="true" />
        <Card tourAnchor="refine-mic-button" className={styles.draftCard}>
          Mic button (fixture target — real anchor id "refine-mic-button", Refine.tsx)
        </Card>
        <CoachMark
          step="first-story"
          currentPath="/refine"
          onAdvance={noopTourHandler}
          onSkip={noopTourHandler}
        />
      </div>

      <div className={styles.draftKind}>
        where-to-next — centered, no anchor (the closing step; plain "Skip tour" label, not the
        welcome variant above)
      </div>
      <div style={STAGE_STYLE_COMPACT}>
        <CoachMark
          step="where-to-next"
          currentPath="/refine"
          onAdvance={noopTourHandler}
          onSkip={noopTourHandler}
        />
      </div>

      <div className={styles.draftKind}>
        see-it-land — anchor-missing note (a real, non-null anchor — "refine-view-toggle" — that
        simply isn't mounted on THIS page; the honest "head over there and I'll be waiting" cue,
        never a crash on a null rect)
      </div>
      <div style={STAGE_STYLE_COMPACT}>
        <CoachMark step="see-it-land" currentPath="/" onAdvance={noopTourHandler} onSkip={noopTourHandler} />
      </div>

      <h3 style={{ margin: '32px 0 8px' }}>Search scenarios panel (ScenarioTruthPanel — L-K)</h3>
      <p className={styles.subtitle}>
        The scenarios management surface, closing R386's registered follow-up. Borrows
        CorpusArchivePanel's archive/undo idiom on purpose: greyed + reversible + an honest count,
        nothing deleted. Every "why" below is the SERVED staleReason mapped to plain language —
        an unrecognised code renders no explanation at all rather than a guess.
      </p>

      <div className={styles.draftKind}>active — a live scenario, offering Retire</div>
      <ScenarioTruthPanel
        scenarios={[scenarioLive]}
        loading={false}
        error={null}
        actionError={null}
        pending={null}
        onRetire={noopScenarioAction}
        onReactivate={noopScenarioAction}
      />

      <div className={styles.draftKind}>
        retired-stale — greyed, struck, with the honest why. The DERIVED one (no retire stamp)
        offers the route back instead of a Reactivate button that could not work; the USER-retired
        one (a real stamp) offers "Bring it back" and his own note verbatim
      </div>
      <ScenarioTruthPanel
        scenarios={[scenarioLive, scenarioRetiredDerived, scenarioRetiredByUser]}
        loading={false}
        error={null}
        actionError={null}
        pending={null}
        defaultShowRetired
        onRetire={noopScenarioAction}
        onReactivate={noopScenarioAction}
      />

      <div className={styles.draftKind}>
        retiring (pending) — one row's action in flight: its label becomes "Retiring…" and every
        button on the panel is held until the refetch lands
      </div>
      <ScenarioTruthPanel
        scenarios={[scenarioLive, scenarioRetiredByUser]}
        loading={false}
        error={null}
        actionError={null}
        pending={{ id: scenarioLive.id, action: 'retire' }}
        defaultShowRetired
        onRetire={noopScenarioAction}
        onReactivate={noopScenarioAction}
      />

      <div className={styles.draftKind}>
        error — a failed retire, surfaced in Meira's voice with the real server message (never a
        silent swallow)
      </div>
      <ScenarioTruthPanel
        scenarios={[scenarioLive]}
        loading={false}
        error={null}
        actionError={describeScenarioActionError('retire', 'not_found')}
        pending={null}
        onRetire={noopScenarioAction}
        onReactivate={noopScenarioAction}
      />

      <div className={styles.draftKind}>loading</div>
      <ScenarioTruthPanel
        scenarios={[]}
        loading
        error={null}
        actionError={null}
        pending={null}
        onRetire={noopScenarioAction}
        onReactivate={noopScenarioAction}
      />

      <div className={styles.draftKind}>empty — no scenarios yet, said plainly</div>
      <ScenarioTruthPanel
        scenarios={[]}
        loading={false}
        error={null}
        actionError={null}
        pending={null}
        onRetire={noopScenarioAction}
        onReactivate={noopScenarioAction}
      />

      <h3 style={{ margin: '32px 0 8px' }}>
        Talk it through — the two-way voice interview on a gap (GapVoiceInterviewView — L-M / W22)
      </h3>
      <p style={{ margin: '0 0 12px', fontSize: 13, opacity: 0.75 }}>
        The third answer path on the MISSING EVIDENCE gap sheet, beside type and dictate. Meira asks
        the gap question out loud, he answers out loud. Only HIS turns can ever be sent — Meira's
        lines render in the transcript and are excluded from the capture by spokenUserAnswer, then
        re-checked by guardSpokenCommit before "Use what I said" is even enabled.
      </p>

      <div className={styles.draftKind}>idle — offered, nothing started, the mic untouched</div>
      <GapVoiceInterviewView
        state="idle"
        turns={[]}
        corrections={[]}
        availability={voiceAvailable}
        error={null}
        {...voiceNoops}
      />

      <div className={styles.draftKind}>listening — live, waiting on him</div>
      <GapVoiceInterviewView
        state="listening"
        turns={voiceTurns.slice(0, 1)}
        corrections={[]}
        availability={voiceAvailable}
        error={null}
        {...voiceNoops}
      />

      <div className={styles.draftKind}>agent-speaking — Meira is talking</div>
      <GapVoiceInterviewView
        state="agent_speaking"
        turns={voiceTurns}
        corrections={[]}
        availability={voiceAvailable}
        error={null}
        {...voiceNoops}
      />

      <div className={styles.draftKind}>
        transcript-ready-to-confirm — his words back, with the term repairs shown and undoable
      </div>
      <GapVoiceInterviewView
        state="ready_to_confirm"
        turns={voiceTurnsRepaired}
        corrections={voiceCorrections}
        availability={voiceAvailable}
        error={null}
        {...voiceNoops}
      />

      <div className={styles.draftKind}>
        error — the live loop stopped, said plainly, with type/dictate still available
      </div>
      <GapVoiceInterviewView
        state="error"
        turns={voiceTurns}
        corrections={[]}
        availability={voiceAvailable}
        error="I couldn't get to the microphone — your browser blocked it. Check the mic permission for this site, or type your answer instead."
        {...voiceNoops}
      />

      <div className={styles.draftKind}>
        unsupported-browser — the option stays visible and explains itself, never a dead button
      </div>
      <GapVoiceInterviewView
        state="unsupported"
        turns={[]}
        corrections={[]}
        availability={voiceUnsupported}
        error={null}
        {...voiceNoops}
      />
    </div>
  );
}
